import React, { useState, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ChevronDown } from "lucide-react"
import { SERVICES, SERVICES_HUB, serviceHref, CONTACT_PROJECT } from "../data/services.js"
import { logout } from "../lib/adminApi.js"
import logo from "../assets/2merative-logo-nav.webp"

// UI hint only (shows admin links). Access is enforced by the server session.
const LOCALSTORAGE_KEY = "isAdmin"

const navItems = [
  { label: "Home", path: "/" },
  { label: "Services", path: SERVICES_HUB, children: SERVICES },
  { label: "Our Work", path: "/portfolio" },
  { label: "Industries", path: "/industries" },
  { label: "Insights", path: "/blog" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
]

const privateNavItems = [
  { label: "Private Health Check", path: "/private-health-check" },
  { label: "Landing Index", path: "/lp-index-72a9" },
]

export default function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const servicesRef = useRef(null)
  const [isAdmin, setIsAdmin] = useState(
    typeof window !== "undefined" &&
      localStorage.getItem(LOCALSTORAGE_KEY) === "true"
  )

  useEffect(() => {
    const sync = () => {
      const v = localStorage.getItem(LOCALSTORAGE_KEY) === "true"
      setIsAdmin(v)
    }
    sync()
    window.addEventListener("storage", sync)
    return () => window.removeEventListener("storage", sync)
  }, [])

  // Close menus whenever the route changes.
  useEffect(() => {
    setMenuOpen(false)
    setServicesOpen(false)
    setMobileServicesOpen(false)
  }, [location.pathname, location.hash, location.search])

  // Desktop services dropdown: close on outside click or Escape.
  useEffect(() => {
    if (!servicesOpen) return

    const onPointerDown = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false)
      }
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setServicesOpen(false)
        servicesRef.current?.querySelector("button")?.focus()
      }
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [servicesOpen])

  const toggleMenu = () => setMenuOpen(!menuOpen)
  const closeMenu = () => setMenuOpen(false)

  const isActive = (path) => {
    const [base, hash] = path.split("#")
    if (hash) return location.pathname === "/" && location.hash === `#${hash}`
    if (base === "/") return location.pathname === "/" && !location.hash
    return location.pathname === base || location.pathname.startsWith(base + "/")
  }

  const isServicesActive = () =>
    isActive(SERVICES_HUB) ||
    SERVICES.some((s) => s.live && isActive(s.path))

  const handleLogout = async () => {
    closeMenu()
    try {
      await logout() // ends the server-side session
    } catch {
      // the hint is cleared regardless; the session also expires server-side
    }
    setIsAdmin(false)
    navigate("/")
  }

  const loginUrl = `/private/login?next=${encodeURIComponent(
    location.pathname + location.search
  )}`

  const linkClass = (active) =>
    `text-sm font-medium transition duration-300 ${
      active ? "text-brand-primary font-semibold" : "text-brand-text hover:text-brand-primary"
    }`

  const focusRing =
    "rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"

  return (
    <header className="fixed top-0 left-0 right-0 bg-white z-[9999] shadow-sm">
      <nav
        aria-label="Main"
        className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-6"
      >
        <Link
          to="/"
          className={`shrink-0 ${focusRing}`}
          onClick={closeMenu}
        >
          <img
            src={logo}
            alt="2merative home"
            width={220}
            height={81}
            className="block h-10 w-auto lg:h-11"
          />
        </Link>

        <div className="hidden lg:flex flex-wrap items-center justify-end gap-x-6 gap-y-2">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} ref={servicesRef} className="relative">
                <button
                  type="button"
                  aria-expanded={servicesOpen}
                  aria-controls="services-menu"
                  onClick={() => setServicesOpen((v) => !v)}
                  className={`inline-flex items-center gap-1 ${linkClass(isServicesActive())} ${focusRing}`}
                >
                  {item.label}
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 transition ${servicesOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {servicesOpen && (
                  <div
                    id="services-menu"
                    className="absolute left-1/2 top-full mt-3 w-72 -translate-x-1/2 rounded-xl2 border border-brand-border bg-white p-2 shadow-cardStrong"
                  >
                    <ul>
                      {item.children.map((service) => (
                        <li key={service.key}>
                          <Link
                            to={serviceHref(service)}
                            className={`block rounded-xl px-4 py-2.5 text-sm text-brand-text hover:bg-brand-primarySoft hover:text-brand-primary ${focusRing}`}
                          >
                            {service.navLabel}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-1 border-t border-brand-border pt-1">
                      <Link
                        to={item.path}
                        className={`block rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-primary hover:bg-brand-primarySoft ${focusRing}`}
                      >
                        All services
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                aria-current={isActive(item.path) ? "page" : undefined}
                className={`${linkClass(isActive(item.path))} ${focusRing}`}
              >
                {item.label}
              </Link>
            )
          )}

          {isAdmin ? (
            <>
              <span className="text-gray-300" aria-hidden="true">|</span>
              {privateNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={`${linkClass(isActive(item.path))} ${focusRing}`}
                >
                  {item.label}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className={`text-sm font-medium text-brand-text hover:text-red-600 transition duration-300 ${focusRing}`}
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to={loginUrl}
              onClick={closeMenu}
              className={`text-xs font-medium text-brand-textSoft hover:text-brand-primary transition duration-300 ${focusRing}`}
            >
              Admin Login
            </Link>
          )}

          <Link
            to={CONTACT_PROJECT}
            className="inline-flex min-h-[44px] items-center rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-button transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Discuss Your Project
          </Link>
        </div>

        <button
          className={`lg:hidden flex h-11 w-11 items-center justify-center text-brand-text ${focusRing}`}
          onClick={toggleMenu}
          aria-label="Toggle Menu"
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {menuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </nav>

      {menuOpen && (
        <div
          id="mobile-menu"
          className="lg:hidden max-h-[calc(100vh-4.5rem)] overflow-y-auto border-t border-brand-border bg-white px-4 pb-6 pt-2 shadow-md"
        >
          <ul className="divide-y divide-brand-border">
            {navItems.map((item) =>
              item.children ? (
                <li key={item.label}>
                  <button
                    type="button"
                    aria-expanded={mobileServicesOpen}
                    aria-controls="mobile-services-menu"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className={`flex w-full items-center justify-between py-3 text-left text-base font-medium ${
                      isServicesActive() ? "text-brand-primary" : "text-brand-text"
                    } ${focusRing}`}
                  >
                    {item.label}
                    <ChevronDown
                      aria-hidden="true"
                      className={`h-5 w-5 transition ${mobileServicesOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {mobileServicesOpen && (
                    <ul id="mobile-services-menu" className="pb-3 pl-4">
                      {item.children.map((service) => (
                        <li key={service.key}>
                          <Link
                            to={serviceHref(service)}
                            onClick={closeMenu}
                            className={`block py-2 text-sm text-brand-textSoft hover:text-brand-primary ${focusRing}`}
                          >
                            {service.navLabel}
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link
                          to={item.path}
                          onClick={closeMenu}
                          className={`block py-2 text-sm font-semibold text-brand-primary ${focusRing}`}
                        >
                          All services
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              ) : (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeMenu}
                    aria-current={isActive(item.path) ? "page" : undefined}
                    className={`block py-3 text-base font-medium ${
                      isActive(item.path)
                        ? "text-brand-primary"
                        : "text-brand-text hover:text-brand-primary"
                    } ${focusRing}`}
                  >
                    {item.label}
                  </Link>
                </li>
              )
            )}
          </ul>

          <Link
            to={CONTACT_PROJECT}
            onClick={closeMenu}
            className="mt-4 flex min-h-[48px] items-center justify-center rounded-full bg-brand-primary px-6 py-3 text-base font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Discuss Your Project
          </Link>

          {isAdmin ? (
            <>
              <div className="border-t my-4" />
              {privateNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={`block py-2 text-sm font-medium ${
                    isActive(item.path)
                      ? "text-brand-primary font-bold"
                      : "text-gray-700 hover:text-brand-primary"
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              <button
                onClick={handleLogout}
                className="block w-full py-2 text-left text-sm font-medium text-gray-700 hover:text-red-600"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to={loginUrl}
              onClick={closeMenu}
              className="mt-4 block text-center text-xs font-medium text-brand-textSoft hover:text-brand-primary"
            >
              Admin Login
            </Link>
          )}
        </div>
      )}
    </header>
  )
}
